import React from 'react';
import Cropper from 'cropperjs';
import { getImageSizeFromFile } from '@cp/utils/browser/image';
import { Global } from '@emotion/react';
import { Box } from '@cp/ds/src/components/box';
import { PlusIcon } from '@cp/ds/src/icons/plus';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { PhotoPickerHandle, PhotoPickerProps } from './model';
import { Slider } from './components/slider';
import { ActionBar } from './components/actionBar';
import { useScaling } from './hooks/useScaling';
import {
  PHOTO_PICKER_CANVAS_SIZE_DEFAULT,
  PHOTO_PICKER_CROPBOX_TO_CANVAS_RATE,
  PHOTO_PICKER_MIN_HEIGHT_DEFAULT,
  PHOTO_PICKER_MIN_WIDTH_DEFAULT,
  PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE,
  PHOTO_PICKER_ROTATE_DEGREES,
} from './const';
export type { PhotoPickerProps } from './model';

export const PhotoPicker = React.forwardRef<PhotoPickerHandle, PhotoPickerProps>(
  (
    {
      imageUrl,
      minWidth = PHOTO_PICKER_MIN_WIDTH_DEFAULT,
      minHeight = PHOTO_PICKER_MIN_HEIGHT_DEFAULT,
      onValidate,
      onChangeImageUrl,
      onLoadingStateChange,
      actionBarPosition = 'right',
      disabled,
      canvasSize = PHOTO_PICKER_CANVAS_SIZE_DEFAULT,
      showDownloadButton,
    },
    ref,
  ) => {
    const cropBoxWidth = canvasSize * PHOTO_PICKER_CROPBOX_TO_CANVAS_RATE;
    const cropBoxHeight = canvasSize * PHOTO_PICKER_CROPBOX_TO_CANVAS_RATE;
    const theme = useTheme();
    const [imgRef, setImgRef] = React.useState<HTMLImageElement | null>(null);
    const [{ scale, scaleMin, scaleMax, isScaleRangeCalculating }, { setScale }] = useScaling({
      cropBoxWidth,
      cropBoxHeight,
      imageUrl,
      minWidth,
      minHeight,
    });
    const cropperInstanceRef = React.useRef<Cropper>();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const scaleRef = React.useRef(scale);
    React.useEffect(() => {
      scaleRef.current = scale;
    }, [scale]);

    React.useEffect(() => {
      if (!imgRef) {
        return;
      }

      if (cropperInstanceRef.current) {
        cropperInstanceRef.current.reset().clear();
      }

      cropperInstanceRef.current = new Cropper(imgRef, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: 'move',
        initialAspectRatio: 1,
        responsive: true,
        restore: true,
        checkCrossOrigin: true,
        checkOrientation: true,
        rotatable: true,
        scalable: true,
        modal: true,
        guides: false,
        center: false,
        highlight: false,
        background: true,
        autoCrop: true,
        autoCropArea: 0.8,
        movable: true,
        zoomable: false,
        zoomOnTouch: false,
        zoomOnWheel: false,
        cropBoxMovable: false,
        cropBoxResizable: false,
        minCropBoxWidth: cropBoxWidth,
        minCropBoxHeight: cropBoxHeight,

        ready() {
          if (cropperInstanceRef.current) {
            cropperInstanceRef.current.scale(scaleRef.current);
          }
        },
      });
    }, [cropBoxWidth, cropBoxHeight, imgRef, setScale]);

    React.useEffect(() => {
      // If cropper is not initialized yet, then wait for him
      let isEffectInstaceActive = true;

      let intervalID: ReturnType<typeof setInterval> | undefined;
      const fn = () => {
        if (!isEffectInstaceActive) {
          if (intervalID) {
            clearInterval(intervalID);
          }
          return;
        }

        if (cropperInstanceRef.current) {
          cropperInstanceRef.current.scale(scale);
          if (intervalID) {
            clearInterval(intervalID);
          }
        }
      };
      fn();
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      intervalID = setInterval(fn, 100);

      return () => {
        isEffectInstaceActive = false;
      };
    }, [scale]);

    React.useImperativeHandle(
      ref,
      () => ({
        getCroppedImage: async () => {
          if (!cropperInstanceRef.current) {
            throw new Error('Cropper is not initialized!');
          }

          if (!imageUrl) {
            throw new Error('imageUrl is empty!');
          }

          const cropData = cropperInstanceRef.current.getData();

          const image = new Image();
          const imageLoadPromise = new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
          });
          image.crossOrigin = 'anonymous';
          image.src = imageUrl;

          await imageLoadPromise;

          const canvas = document.createElement('canvas');
          canvas.width = cropData.width / cropData.scaleX;
          canvas.height = cropData.height / cropData.scaleY;

          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Canvas context 2d is not available');
          }

          const sx = cropData.x / cropData.scaleX;
          const sy = cropData.y / cropData.scaleX;
          ctx.drawImage(image, sx, sy, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);

          return canvas.toDataURL('image/jpeg', 1);
        },
      }),
      [imageUrl],
    );

    const handleAddImage = () => {
      if (!fileInputRef.current) {
        throw new Error('file input is not available');
      }

      fileInputRef.current.value = '';
      fileInputRef.current.click();
    };

    const handleImageFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!fileInputRef.current) {
        throw new Error('file input is not available');
      }

      let file: File | undefined;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((event as any).dataTransfer?.files?.length) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        file = (event as any).dataTransfer.files[0];
      }

      if (fileInputRef.current.files?.length) {
        file = fileInputRef.current.files[0];
      }

      if (!file) {
        return;
      }

      onLoadingStateChange?.(true);

      try {
        const imageSize = await getImageSizeFromFile(file);

        await onValidate?.(file);

        let newImageUrl = await new Promise<string>((resolve, reject) => {
          if (!file) {
            throw new Error('image file is unavailable');
          }

          // Read image data from selected file
          const reader = new FileReader();
          reader.onabort = reject;
          reader.onerror = reject;
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        if (imageSize.width > PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE || imageSize.height > PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE) {
          const image = new Image();
          const imageLoadPromise = new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
          });
          image.src = newImageUrl;

          await imageLoadPromise;

          const canvas = document.createElement('canvas');
          let width = image.width;
          let height = image.height;

          if (width > height) {
            if (width > PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE) {
              height *= PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE / width;
              width = PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE;
            }
          } else {
            if (height > PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE) {
              width *= PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE / height;
              height = PHOTO_PICKER_PICKED_IMAGE_MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
          newImageUrl = canvas.toDataURL('image/jpeg', 1);
        }

        onChangeImageUrl(newImageUrl);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        alert(err.message);
      }

      onLoadingStateChange?.(false);
    };

    return (
      <Box>
        {imageUrl ? (
          isScaleRangeCalculating ? null : (
            <>
              <Global
                styles={{
                  ['.cropper-container']: {
                    direction: 'ltr',
                    fontSize: 0,
                    lineHeight: 0,
                    position: 'relative',
                    msTouchAction: 'none',
                    touchAction: 'none',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  },
                  ['.cropper-container img']: {
                    display: 'block',
                    height: '100%',
                    imageOrientation: '0deg',
                    maxHeight: 'none !important',
                    maxWidth: 'none !important',
                    minHeight: '0 !important',
                    minWidth: '0 !important',
                    width: '100%',
                  },
                  ['.cropper-wrap-box, .cropper-canvas, .cropper-drag-box, .cropper-crop-box, .cropper-modal']: {
                    bottom: 0,
                    left: 0,
                    position: 'absolute',
                    right: 0,
                    top: 0,
                  },
                  ['.cropper-wrap-box, .cropper-canvas']: {
                    overflow: 'hidden',
                  },
                  ['.cropper-drag-box']: {
                    backgroundColor: '#fff',
                    opacity: 0,
                  },
                  ['.cropper-modal']: {
                    backgroundColor: '#fff',
                    opacity: 0.5,
                  },
                  ['.cropper-view-box']: {
                    display: 'block',
                    height: '100%',
                    overflow: 'hidden',
                    width: '100%',
                  },
                  ['.cropper-move']: {
                    cursor: 'move',
                  },
                  ['.cropper-face, .cropper-line, .cropper-point']: {
                    display: 'block',
                    height: '100%',
                    opacity: 0.1,
                    position: 'absolute',
                    width: '100%',
                  },
                  ['.cropper-face']: {
                    backgroundColor: '#fff',
                    left: 0,
                    top: 0,
                  },
                }}
              />
              <Box
                sx={{
                  width: canvasSize,
                  height: canvasSize,
                  maxWidth: '100%',
                  maxHeight: '100%',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    width: canvasSize,
                    height: canvasSize,
                    maxWidth: '100%',
                    maxHeight: '100%',
                    borderRadius: `${theme.borderRadius.large}px`,
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <img
                    alt=""
                    crossOrigin="anonymous"
                    onError={() => onChangeImageUrl(undefined)}
                    ref={(newImgRef) => setImgRef(newImgRef)}
                    src={imageUrl}
                    style={{ maxWidth: '100%', position: 'absolute', zIndex: -1, visibility: 'hidden' }}
                  />
                  {disabled && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        backgroundColor: 'gray light',
                        opacity: 0.3,
                      }}
                    />
                  )}
                </Box>

                {!disabled && actionBarPosition === 'right' && (
                  <ActionBar
                    imageUrl={imageUrl}
                    onDelete={() => onChangeImageUrl(undefined)}
                    onRotate={() => cropperInstanceRef.current?.rotate(PHOTO_PICKER_ROTATE_DEGREES)}
                    orientation="vertical"
                    showDownloadButton={showDownloadButton}
                    sx={{ position: 'absolute', right: -56, top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
              </Box>
              {!disabled && (
                <Slider
                  max={scaleMax}
                  min={scaleMin}
                  onChange={(_, newScale) => setScale(newScale as number)}
                  step={0.01}
                  sx={{ mt: 3.125 }}
                  value={scale}
                />
              )}
              {!disabled && actionBarPosition === 'bottom' && (
                <ActionBar
                  imageUrl={imageUrl}
                  onDelete={() => onChangeImageUrl(undefined)}
                  onRotate={() => cropperInstanceRef.current?.rotate(PHOTO_PICKER_ROTATE_DEGREES)}
                  orientation="horizontal"
                  showDownloadButton={showDownloadButton}
                  sx={{ mt: 1.5, justifyContent: 'center' }}
                />
              )}
            </>
          )
        ) : (
          <Box
            onClick={disabled ? undefined : handleAddImage}
            role="button"
            sx={{
              width: canvasSize,
              height: canvasSize,
              maxWidth: '100%',
              maxHeight: '100%',
              border: `1px solid ${theme.palette['gray middle']}`,
              borderRadius: `${theme.borderRadius.large}px`,
              backgroundColor: theme.palette['gray ultra light'],
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            tabIndex={0}
          >
            <PlusIcon sx={{ fontSize: '3rem', color: disabled ? 'gray light' : undefined }} />
            <input
              key={5}
              onChange={handleImageFilePicked}
              ref={fileInputRef}
              style={{ width: '0px', height: '0px', zIndex: '-5', opacity: 0 }}
              type="file"
              // accept=".jpg, .jpeg, .png, .bmp"
            />
          </Box>
        )}
      </Box>
    );
  },
);
